import { motion } from "framer-motion";
import {
  Siren,
  Wrench,
  Droplets,
  Search,
  PipetteIcon,
  Bath,
  Flame,
  Star,
  Video,
  Building2,
} from "lucide-react";

const services = [
  {
    icon: Droplets,
    title: "Drain Cleaning & Unblocking",
    desc: "High-pressure drain cleaning to clear blockages and keep your system flowing freely.",
    speciality: true,
  },
  {
    icon: Video,
    title: "CCTV Drainage Surveys",
    desc: "Advanced digital colour camera surveys to accurately inspect and map drainage systems, identifying root intrusion, cracked pipework, displaced joints and blockages.",
  },
  {
    icon: Building2,
    title: "Commercial & Project Work",
    desc: "From small business premises to larger commercial projects, Conan can project manage and coordinate trusted subcontractors to deliver the full scope of work.",
  },
  {
    icon: Bath,
    title: "Bathroom Installations",
    desc: "Complete bathroom fit-outs — showers, suites, tiling-ready plumbing and finishing.",
  },
  {
    icon: Siren,
    title: "Emergency Call-Outs",
    desc: "24/7 rapid response for burst pipes, floods and any urgent plumbing emergency across Edinburgh.",
  },
  {
    icon: Wrench,
    title: "General Plumbing Repairs",
    desc: "From blocked toilets, basins and sinks to taps, valves, fittings and everyday plumbing fixes — done properly, first time.",
  },
  {
    icon: Search,
    title: "Leak Detection & Repair",
    desc: "Accurate leak tracing with minimal disruption — find it, fix it, dry it out.",
  },
  {
    icon: PipetteIcon,
    title: "Pipe Repairs & Replacement",
    desc: "From small repairs to full pipework replacement using modern, durable materials.",
  },
  {
    icon: Flame,
    title: "Central Heating",
    desc: "Radiators, valves, system flush outs and central heating repairs to keep your home warm and running efficiently.",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.06 },
  }),
};

const Services = () => {
  return (
    <section
      id="services"
      className="relative py-20 md:py-32 bg-[#0B132B]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14 md:mb-20">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D47A4A] mb-4">
            / 01 — Our Services
          </p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] tracking-tight">
            EVERY JOB.
            <br />
            <span className="text-[#D47A4A]">DONE RIGHT.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#A0ABC0] max-w-2xl">
            From a dripping tap at midnight to a full bathroom install — we cover
            the full scope of domestic plumbing and drainage in Edinburgh.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                className={`group relative bg-[#151D33] border ${
                  s.speciality
                    ? "border-[#D47A4A]/70 ring-1 ring-[#D47A4A]/30"
                    : "border-[#2C374D] hover:border-[#D47A4A]/60"
                } p-6 md:p-7 rounded-sm transition-all duration-300 hover:-translate-y-1`}
                data-testid={`service-card-${i + 1}`}
              >
                {s.speciality && (
                  <div
                    className="absolute -top-3 left-6 inline-flex items-center gap-1.5 bg-[#D47A4A] text-[#0B132B] text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-sm shadow-lg"
                    data-testid="service-speciality-badge"
                  >
                    <Star className="w-3 h-3 fill-current" />
                    Our Speciality
                  </div>
                )}
                <div className="mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0B132B] border border-[#2C374D] group-hover:border-[#D47A4A] group-hover:bg-[#D47A4A]/10 transition-colors rounded-sm">
                    <Icon className="w-6 h-6 text-[#D47A4A]" />
                  </div>
                </div>
                <h3 className="font-display text-xl md:text-2xl text-white tracking-wide mb-2">
                  {s.title.toUpperCase()}
                </h3>
                <p className="text-sm text-[#A0ABC0] leading-relaxed">{s.desc}</p>
                <div className="absolute bottom-0 left-0 h-px w-0 bg-[#D47A4A] group-hover:w-full transition-all duration-500" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
